"use client"

import { useState, useEffect, useId } from 'react';
import { Badge } from "src/components/ui/badge";
import { Button } from "src/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "src/components/ui/card";
import { Progress } from "src/components/ui/progress";
import { Separator } from "src/components/ui/separator";
import { Activity, Search, AlertTriangle, X } from "lucide-react";
import { useStockfishAnalysis } from './hooks/useStockfishAnalysis';
import { useChessGameContext } from 'react-chess-game-fix';

function formatEvaluation(evaluation) {
  if (evaluation > 0) {
    return {
      text: `+${evaluation.toFixed(2)}`,
      color: "text-green-700",
      bgColor: "bg-green-100",
    }
  } else if (evaluation < 0) {
    return {
      text: evaluation.toFixed(2),
      color: "text-red-700",
      bgColor: "bg-red-100",
    }
  } else if (typeof evaluation === 'string') {
    return {
      text: evaluation,
      color: "text-gray-700",
      bgColor: "bg-gray-100",
    }
  } else {
    return {
      text: "0.00",
      color: "text-gray-700",
      bgColor: "bg-gray-100",
    }
  }
}

function formatNodes(nodes) {
  if (nodes >= 1000000) {
    return `${(nodes / 1000000).toFixed(1)}M`
  } else if (nodes >= 1000) {
    return `${(nodes / 1000).toFixed(0)}K`
  }
  return nodes?.toString()
}

export function ChessAnalysisPanel({ wrongMove=undefined, isPremium=true, puzzleIndex }) { //Maybe make isPremium false by default
  const { currentFen } = useChessGameContext();
  const [showAnalysis, setShowAnalysis] = useState(false)
  const [showRefutation, setShowRefutation] = useState(false)
  const [show, setShow] = useState('all');
  const depth = 19;
  // console.log(`frozen fen: ${frozenFen}`)

  useEffect(() => {
    setShow('all');
    setShowAnalysis(false);
    setShowRefutation(false);
  }, [puzzleIndex]);

  const showAnalysisPanel   = show === 'all' || show === 'analysis';
  const showRefutationPanel = show === 'all' || show === 'refutation';

  return (
    <div className="min-w-0 space-y-4 text-left">
      <div className={showAnalysisPanel ? '' : 'hidden'}>
        <AnalysisPanel currentFen={currentFen} depth={depth} showAnalysis={showAnalysis} setShowAnalysis={setShowAnalysis} setShow={setShow} />
      </div>
      <div className={showRefutationPanel ? '' : 'hidden'}>
        <RefutationPanel wrongMove={wrongMove} depth={depth} showRefutation={showRefutation} setShowRefutation={setShowRefutation} setShow={setShow} isPremium={isPremium} />
      </div>
    </div>
  );
}

function AnalysisPanel({ currentFen, depth=19, showAnalysis, setShowAnalysis, setShow}) {
  const pvNum = 3;
  const engineName = 'Stockfish 17'
  const { infoList, analyse } = useStockfishAnalysis();

  const handleAnalyse = () => {
    setShow('analysis');
    analyse(currentFen, depth, pvNum);
    setShowAnalysis(true)
  };

  const handleCloseAnalysis = () => {
    setShowAnalysis(false)
    setShow('all');
  }

  // Latest primary info
  const primary = [...infoList].reverse().find(i => i.multipv === 1) || {};

  return (
    <div className="w-full min-w-0">
      {!showAnalysis && (
        <Card className="min-w-0 gap-4 rounded-2xl border-slate-200 bg-white p-5 text-slate-800 shadow-sm">
          <CardContent className="flex items-start gap-3 p-0">
            <div className="min-w-0 flex-1">
              <h2 className="mb-2 text-base font-semibold text-slate-800">Position analysis</h2>
              <p className="mb-0 text-sm leading-relaxed text-slate-600">
                Explore the current position with Stockfish.
              </p>
            </div>
            <Button
              onClick={handleAnalyse}
              variant="brand" size="action" className="m-0 w-12 p-0"
              aria-label="Analyze position" title="Analyze position"
            >
              <Search aria-hidden="true" />
            </Button>
          </CardContent>
        </Card>
      )}

      {showAnalysis && (
      <Card className="min-w-0 gap-4 rounded-2xl border-slate-200 bg-white p-5 text-slate-800 shadow-sm">
        <CardHeader className="p-0">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="min-w-0 text-base font-semibold leading-snug flex items-center gap-2">
              <Activity className="h-5 w-5 text-blue-600" />
              Engine Analysis
            </CardTitle>
            <Button variant="neutral" size="action" onClick={handleCloseAnalysis} className="m-0 w-12 border-solid p-0" aria-label="Close analysis" title="Close analysis">
              <X aria-hidden="true" />
            </Button>
            
          </div>

          <div className="mt-3 flex items-center gap-3">
            <dl className="m-0 grid min-w-0 flex-1 grid-cols-2 gap-3">
              <div>
                <dt className="text-xs text-slate-600">Depth</dt>
                <dd className="m-0 font-mono text-sm font-semibold text-slate-800">{depth}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-600">Nodes</dt>
                <dd className="m-0 font-mono text-sm font-semibold text-slate-800">{formatNodes(primary.nodes) || '..'}</dd>
              </div>
            </dl>
            <Button
              onClick={handleAnalyse}
              variant="brand" size="action" className="m-0 ml-auto w-12 p-0"
              aria-label="Analyze current position again" title="Analyze current position again"
            >
              <Search aria-hidden="true" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="min-w-0 space-y-4 p-0">
          {/* Best Move Section */}
          <div className="mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <span className="text-sm font-medium text-slate-600">Best Move</span>
            <Badge variant="outline" className="font-mono text-sm">
              {engineName}
            </Badge>
          </div>
            <div className="rounded-xl border border-sky-100 bg-sky-50 p-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-4">
                  <div className="font-mono text-xl font-semibold text-slate-800">
                    {primary.pv?.[0] ?? '..'}
                  </div>
                  <div className="text-sm text-slate-600">
                    {primary.pv?.slice(1, 2).join(" ") ?? '..'}{primary.pv?.length > 3 ? '...' : ''}
                  </div>
                </div>
                <div className="text-right">
                  <div
                    className={`font-mono text-xl font-semibold ${formatEvaluation(primary.score).color}`}
                  >
                    {formatEvaluation(primary.score).text}
                  </div>
                  <div className="text-sm text-slate-600 mt-1">
                    Depth {primary.depth}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/*Principal Variations Section*/}
          <div className="text-sm font-medium text-slate-600 mb-3">Principal Variations</div>

          {infoList.slice(-1 * pvNum).map((analysis, index) => {
            const evalFormat = formatEvaluation(analysis.score)
            const progressValue = Math.max(10, Math.min(90, 50 + analysis.score * 10))

            return (
              <div key={index} className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <Badge
                      variant="secondary"
                      className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold"
                    >
                      {index + 1}
                    </Badge>
                    <Badge className={`${evalFormat.color} ${evalFormat.bgColor} border-0 font-mono text-sm px-3 py-1`}>
                      {evalFormat.text}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-4 text-xs text-slate-600">
                    <span>D{analysis.depth}</span>
                    <span>{formatNodes(analysis.nodes)}</span>
                    {/* <span>{formatTime(analysis.time)}</span> */}
                  </div>
                </div>

                <div className="relative">
                  <Progress value={progressValue} className="h-1 mb-3 bg-black z-10" />
                  <div
                    className="absolute top-0 w-full h-1 bg-gray-300 rounded-full"
                    style={{ left: "50%", transform: "translateX(-50%)" }}
                  />
                </div>

                <div className="bg-slate-50 rounded-lg p-4 overflow-hidden">
                  <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                    <div className="font-mono text-sm leading-7 break-words">
                      {analysis.pv?.map((move, moveIndex) => (
                        <span
                          key={moveIndex}
                          className="mr-2 inline-block font-medium text-slate-800"
                        >
                          {move}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {index < infoList.length - 1 && <Separator className="my-4" />}
              </div>
            )
          })}
        </CardContent>
      </Card>
      )}
    </div>
  )
}

// Separate Refutation Panel Component
function RefutationPanel({ wrongMove=undefined, showRefutation, setShowRefutation, setShow, isPremium }) {
  const helpId = useId();
  const {
    best, wrong,
    refute,
  } = useStockfishAnalysis();
  const { currentFen } = useChessGameContext();


  const handleShowRefutation = () => {
    setShowRefutation(true)
    setShow('refutation');
    refute(currentFen, wrongMove);
    
  }

  const handleCloseRefutation = () => {
    setShowRefutation(false)
    setShow('all');
  }

  // console.log(refuteResult)
  if (!showRefutation) {
    return (
      <div className="w-full min-w-0">
        <Card className="min-w-0 gap-4 rounded-2xl border-slate-200 bg-white p-5 text-slate-800 shadow-sm">
          <CardContent className="p-0">
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <h2 className="mb-2 text-base font-semibold text-slate-800">Move refutation</h2>
                <p className="mb-0 text-sm leading-relaxed text-slate-600">
                  See why a move fails and find the best response.
                </p>
              </div>
              <Button onClick={handleShowRefutation} variant="brand" size="action" className="m-0 w-12 p-0"
                disabled={!isPremium || !wrongMove}
                aria-label={wrongMove ? `Refute move ${wrongMove}` : 'Refute move'}
                title={wrongMove ? `Refute move ${wrongMove}` : 'Refute move'}
                aria-describedby={helpId}>
                <AlertTriangle aria-hidden="true" />
              </Button>
            </div>
            <p id={helpId} className="mb-0 mt-3 text-sm leading-relaxed text-slate-600">
              {!isPremium ? 'Move refutation is available with Premium.' : !wrongMove ? 'Available after an incorrect move.' : `Your move: ${wrongMove}`}
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="w-full min-w-0">
      <Card className="min-w-0 gap-4 rounded-2xl border-slate-200 bg-white p-5 text-slate-800 shadow-sm">
        <CardHeader className="p-0">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="min-w-0 text-base font-semibold leading-snug flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Move Refutation
            </CardTitle>
            <Button variant="neutral" size="action" onClick={handleCloseRefutation} className="m-0 w-12 border-solid p-0" aria-label="Close refutation" title="Close refutation">
              <X aria-hidden="true" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="min-w-0 space-y-6 p-0">
          {/* Wrong Move Section */}
          <div>
            <div className="text-sm font-medium text-slate-600 mb-3">Your Move</div>
            <div className="rounded-xl border border-red-200 bg-red-50 p-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-4">
                  <div className="font-mono text-xl font-semibold text-slate-800">{wrongMove}</div>
                </div>
                <div className="text-right">
                  <div
                    className={`font-mono text-xl font-semibold ${formatEvaluation(wrong?.score).color}`}
                  >
                    {wrong?.score != null ? formatEvaluation(wrong.score).text : <LoadingDots />}
                  </div>
                  <div className="text-sm text-slate-600 mt-1">After your move</div>
                </div>
              </div>
            </div>
          </div>

          {/* Refutation Line */}
          <div>
            <div className="text-sm font-medium text-slate-600 mb-3">How Opponent Punishes</div>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Badge className="bg-red-100 text-red-700 border-0 font-mono text-sm px-3 py-1">
                  {wrong?.score != null ? formatEvaluation(wrong.score).text : <LoadingDots />}
                </Badge>
                <span className="text-sm text-slate-600">Opponent's best response</span>
              </div>

              <div className="relative mb-3">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-red-600 h-2 rounded-full transition-all duration-300 ease-in-out"
                    style={{ width: `${Math.max(10, Math.min(90, 50 + wrong?.score * 10))}%` }}
                  />
                </div>
                <div
                  className="absolute top-0 w-0.5 h-2 bg-gray-800 rounded-full"
                  style={{ left: "50%", transform: "translateX(-50%)" }}
                />
              </div>

              <div className="bg-red-50 rounded-lg p-4 overflow-hidden border border-red-100">
                <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                  <div className="font-mono text-sm leading-7 break-words">
                    {wrong?.pv.slice(1).map((move, moveIndex) => (
                      <span
                        key={moveIndex}
                        className="mr-2 inline-block font-medium text-red-800"
                      >
                        {move}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Best Move Section */}
          <div>
            <div className="text-sm font-medium text-slate-600 mb-3">Correct Move</div>
            <div className="rounded-xl border border-green-200 bg-green-50 p-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-4">
                  <div className="font-mono text-xl font-semibold text-slate-800">{best?.pv[0]}</div>
                </div>
                <div className="text-right">
                  <div
                    className={`font-mono text-xl font-semibold ${formatEvaluation(best?.score).color}`}
                  >
                    {best?.score != null ? formatEvaluation(best.score).text : <LoadingDots />}
                  </div>
                  <div className="text-sm text-slate-600 mt-1">After best move</div>
                </div>
              </div>
            </div>
          </div>

          {/* Best Line */}
          <div>
            <div className="text-sm font-medium text-slate-600 mb-3">Best Continuation</div>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Badge className="bg-green-100 text-green-700 border-0 font-mono text-sm px-3 py-1">
                  {best?.score != null ? formatEvaluation(best.score).text : <LoadingDots />}
                </Badge>
                <span className="text-sm text-slate-600">Optimal play for both sides</span>
              </div>

              <div className="relative mb-3">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full transition-all duration-300 ease-in-out"
                    style={{ width: `${Math.max(10, Math.min(90, 50 + best?.score * 10))}%` }}
                  />
                </div>
                <div
                  className="absolute top-0 w-0.5 h-2 bg-gray-800 rounded-full"
                  style={{ left: "50%", transform: "translateX(-50%)" }}
                />
              </div>

              <div className="bg-green-50 rounded-lg p-4 overflow-hidden border border-green-100">
                <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                  <div className="font-mono text-sm leading-7 break-words">
                    {best?.pv.map((move, moveIndex) => (
                      <span
                        key={moveIndex}
                        className="mr-2 inline-block font-medium text-green-800"
                      >
                        {move}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Evaluation Difference */}
          {typeof best?.score !== 'string' && (
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <div className="text-center">
              <div className="text-sm text-slate-600 mb-2">Evaluation Difference</div>
              <div className="font-mono text-xl font-semibold text-blue-700">
                {wrong?.score != null ? (best?.score - wrong?.score).toFixed(2) : <LoadingDots />} points
              </div>
              <div className="text-sm text-slate-600 mt-1">
                Your move was {wrong?.score != null ? (best?.score - wrong?.score).toFixed(2) : <LoadingDots />} points worse
              </div>
            </div>
          </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function LoadingDots({ className = "" }) {
  return (
    <span className={`inline-flex space-x-1 ${className}`}>
      <span className="animate-bounce [animation-delay:-0.3s]">.</span>
      <span className="animate-bounce [animation-delay:-0.15s]">.</span>
      <span className="animate-bounce">.</span>
    </span>
  );
}
